import React, { useMemo, useState } from "react";
import { Type, Braces, Image as ImageIcon, Minus, SeparatorVertical, Square, QrCode, Barcode, Table2, PanelTop, Plus, Trash2, ChevronUp, ChevronDown, Search, Calculator, Copy } from "lucide-react";
import { fieldCatalog } from "../sample-data";
import { fieldLabel, getDocType } from "../doc-types";
import { newBand, newColumn, newElement, cloneWithNewIds, uid } from "../schema";
import { ZP } from "../compile";
import FormulaInput from "./formula-input";
import { Check } from "./ui";

const TOOLS = [
  { type: "text", label: "Text", icon: <Type size={16} />, extra: { content: "Text" } },
  { type: "field", label: "Field / Formula", icon: <Braces size={16} />, extra: { expr: "" } },
  { type: "image", label: "Image", icon: <ImageIcon size={16} /> },
  { type: "image", label: "Company logo", icon: <ImageIcon size={16} />, extra: { srcExpr: "store.company_logo", placeholder: "Logo", w: 30, h: 15 } },
  { type: "line", label: "Line", icon: <Minus size={16} /> },
  { type: "line", label: "Vertical line", icon: <SeparatorVertical size={16} />, extra: { orientation: "v", w: 2, h: 20 } },
  { type: "rect", label: "Box", icon: <Square size={16} /> },
  { type: "qr", label: "QR code", icon: <QrCode size={16} /> },
  { type: "barcode", label: "Barcode", icon: <Barcode size={16} /> },
];

function setDrag(e, type, extra) {
  e.dataTransfer.setData("application/x-ib", JSON.stringify({ type, extra }));
  e.dataTransfer.effectAllowed = "copy";
}

function InsertTab({ template, selection, update, setSelection, contentWidth }) {
  const targetBand = () => {
    const b = template.bands.find((x) => x.id === selection.bandId && x.type === "fixed");
    return b || template.bands.find((x) => x.type === "fixed");
  };
  const add = (type, extra) => {
    let band = targetBand();
    let newBandObj = null;
    if (!band) {
      newBandObj = newBand("fixed", contentWidth, { name: "Section" });
      band = newBandObj;
    }
    const el = newElement(type, { x: 2, y: 2 }, extra || {});
    update((t) => {
      if (newBandObj) t.bands.push(newBandObj);
      const b = t.bands.find((x) => x.id === band.id);
      b.elements.push(el);
      if (el.y + el.h > b.height) b.height = el.y + el.h + 1;
    });
    setSelection({ bandId: band.id, ids: [el.id], colId: null });
  };
  const addBand = (type) => {
    const b = newBand(type, contentWidth, type === "table" ? {} : { name: "Section" });
    const idx = template.bands.findIndex((x) => x.id === selection.bandId);
    update((t) => {
      if (idx >= 0) t.bands.splice(idx + 1, 0, b);
      else t.bands.push(b);
    });
    setSelection({ bandId: b.id, ids: type === "table" ? [b.elements[0].id] : [], colId: null });
  };
  return (
    <div className="p-3 flex flex-col gap-3">
      <div>
        <div className="text-[11px] font-semibold text-344054 mb-2">Elements</div>
        <div className="grid grid-cols-3 gap-1.5">
          {TOOLS.map((t) => (
            <button
              key={t.label}
              type="button"
              draggable
              onDragStart={(e) => setDrag(e, t.type, t.extra)}
              onClick={() => add(t.type, t.extra)}
              className="flex flex-col items-center gap-1 border border-EAECF0 rounded p-2 text-[10px] text-344054 hover:border-primary hover:text-primary bg-white"
              title="Click to add, or drag onto the page"
            >
              {t.icon}
              <span className="leading-tight text-center">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-344054 mb-2">Sections</div>
        <div className="grid grid-cols-2 gap-1.5">
          <button type="button" className="flex items-center gap-2 border border-EAECF0 rounded p-2 text-[11px] text-344054 hover:border-primary hover:text-primary" onClick={() => addBand("fixed")}>
            <PanelTop size={16} /> Free section
          </button>
          <button type="button" className="flex items-center gap-2 border border-EAECF0 rounded p-2 text-[11px] text-344054 hover:border-primary hover:text-primary" onClick={() => addBand("table")}>
            <Table2 size={16} /> Table section
          </button>
        </div>
        <p className="text-[10px] text-667085 mt-2 leading-snug">
          A free section is a fixed-height area where you place elements anywhere. A table section grows with the rows (items, batches, GST summary,
          payments) and pushes everything below it down.
        </p>
      </div>
      <div className="text-[10px] text-667085 leading-snug border-t border-EAECF0 pt-2">
        <b>Tips:</b> double-click text to edit · Shift-click to select several · arrows nudge (Shift = 5 mm) · Alt+click selects the element underneath · hold Alt while dragging to turn off snapping · drag
        an element into another section to move it there.
      </div>
    </div>
  );
}

function FieldsTab({ template, selection, update, setSelection, contentWidth }) {
  const [q, setQ] = useState("");
  const [withLabel, setWithLabel] = useState(true);
  const groups = useMemo(() => fieldCatalog(template.docType), [template.docType]);
  const dt = getDocType(template.docType);

  const selectedTable = (() => {
    for (const b of template.bands)
      for (const e of b.elements || []) if (e.type === "table" && selection.ids.includes(e.id)) return { band: b, table: e };
    return null;
  })();

  const exprFor = (g, f) => (g.row ? f : g.list ? `JOIN(${g.root}.${f}, ", ")` : `${g.root}.${f}`);

  const addField = (g, f) => {
    if (g.row) {
      if (!selectedTable) return;
      // add as a column of the selected table
      const key = f.replace(/^item_batch_|^item_/, "").slice(0, 20) || "col";
      const used = new Set((selectedTable.table.columns || []).map((c) => c.key));
      let k = key;
      let i = 2;
      while (used.has(k)) k = key + i++;
      const col = newColumn({ key: k, header: fieldLabel(f, dt.prefix), expr: f, width: 10 });
      update((t) => {
        for (const b of t.bands) for (const e of b.elements || []) if (e.id === selectedTable.table.id) e.columns.push(col);
      });
      setSelection({ bandId: selectedTable.band.id, ids: [selectedTable.table.id], colId: col.id });
      return;
    }
    const band = template.bands.find((x) => x.id === selection.bandId && x.type === "fixed") || template.bands.find((x) => x.type === "fixed");
    if (!band) return;
    const label = fieldLabel(f, dt.prefix);
    const el = withLabel ? newElement("text", { x: 2, y: 2 }, { content: `**${label}:** {{ ${exprFor(g, f)} }}`, w: 60 }) : newElement("field", { x: 2, y: 2 }, { expr: exprFor(g, f) });
    update((t) => t.bands.find((x) => x.id === band.id).elements.push(el));
    setSelection({ bandId: band.id, ids: [el.id], colId: null });
  };

  const lower = q.toLowerCase();
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 pb-2 flex flex-col gap-2 border-b border-EAECF0">
        <div className="relative">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-667085" />
          <input className="w-full border border-EAECF0 rounded pl-7 pr-2 py-1 text-xs" placeholder="Search fields" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Check checked={withLabel} onChange={setWithLabel} label="Insert with label" />
        <p className="text-[10px] text-667085 leading-snug">Click or drag a field onto the page. Row fields become columns of the selected table.</p>
      </div>
      <div className="flex-1 overflow-auto">
        {groups.map((g) => {
          const fields = g.fields.filter((f) => !lower || f.toLowerCase().includes(lower) || fieldLabel(f, dt.prefix).toLowerCase().includes(lower));
          if (!fields.length) return null;
          return (
            <div key={g.id} className="border-b border-EAECF0">
              <div className="px-3 py-1.5 text-[11px] font-semibold text-000000 bg-nav-head-bg flex justify-between">
                <span>{g.title}</span>
                {g.row && <span className={`font-normal ${selectedTable ? "text-primary" : "text-667085"}`}>{selectedTable ? "→ add column" : "select a table"}</span>}
              </div>
              {fields.map((f) => {
                const expr = exprFor(g, f);
                return (
                  <div
                    key={f}
                    draggable={!g.row}
                    onDragStart={(e) =>
                      setDrag(
                        e,
                        withLabel ? "text" : "field",
                        withLabel ? { content: `**${fieldLabel(f, dt.prefix)}:** {{ ${expr} }}`, w: 60 } : { expr }
                      )
                    }
                    onClick={() => addField(g, f)}
                    className={`px-3 py-1 text-[11px] flex flex-col cursor-pointer hover:bg-f3f8ff ${g.row && !selectedTable ? "opacity-60" : ""}`}
                    title={expr}
                  >
                    <span className="text-000000">{fieldLabel(f, dt.prefix)}</span>
                    <span className="font-mono text-[10px] text-667085 truncate">{expr}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EL_NAMES = { text: "Text", field: "Field", image: "Image", line: "Line", rect: "Box", qr: "QR code", barcode: "Barcode", table: "Table" };

// Short description of an element for the element list.
function elementLabel(el) {
  const raw = el.type === "text" ? el.content : el.type === "field" || el.type === "qr" || el.type === "barcode" ? el.expr : el.type === "image" ? el.srcExpr || (el.src ? "image" : "") : el.type === "table" ? el.name : "";
  const clean = String(raw || "").replace(/\*\*|__/g, "").replace(/\{\{\s*([\s\S]*?)\s*\}\}/g, "[$1]").replace(/\s+/g, " ").trim();
  return clean || EL_NAMES[el.type] || el.type;
}

function SectionsTab({ template, selection, update, setSelection }) {
  const move = (i, dir) =>
    update((t) => {
      const j = i + dir;
      if (j < 0 || j >= t.bands.length) return;
      const [b] = t.bands.splice(i, 1);
      t.bands.splice(j, 0, b);
    });
  return (
    <div className="p-2 flex flex-col gap-1">
      <p className="text-[10px] text-667085 leading-snug px-1 pb-1">
        Click an element in the list to select it, even when it is hidden behind others. On the page, Alt+click selects the element underneath.
      </p>
      {template.bands.map((b, i) => (
        <React.Fragment key={b.id}>
        <div
          className={`flex items-center gap-1 border rounded px-2 py-1.5 text-[11px] cursor-pointer ${selection.bandId === b.id ? "border-primary bg-f3f8ff" : "border-EAECF0 bg-white"}`}
          onClick={() => setSelection({ bandId: b.id, ids: [], colId: null })}
        >
          {b.type === "table" ? <Table2 size={14} className="text-667085" /> : <PanelTop size={14} className="text-667085" />}
          <span className="flex-1 truncate">
            {b.name || "Section"}
            <span className="text-667085">
              {b.type === "fixed" ? ` · ${b.height} mm` : ""}
              {b.repeat === "top" ? " · every page top" : b.repeat === "bottom" ? " · every page bottom" : ""}
              {b.visibleIf ? " · conditional" : ""}
            </span>
          </span>
          <button type="button" className="p-0.5 hover:text-primary disabled:opacity-30" disabled={i === 0} onClick={(e) => {
              e.stopPropagation();
              move(i, -1);
            }} title="Move up">
            <ChevronUp size={14} />
          </button>
          <button type="button" className="p-0.5 hover:text-primary disabled:opacity-30" disabled={i === template.bands.length - 1} onClick={(e) => {
              e.stopPropagation();
              move(i, 1);
            }} title="Move down">
            <ChevronDown size={14} />
          </button>
          <button
            type="button"
            className="p-0.5 hover:text-primary"
            title="Duplicate"
            onClick={(e) => {
              e.stopPropagation();
              const copy = cloneWithNewIds(b);
              copy.id = uid("bd");
              copy.name = (b.name || "Section") + " copy";
              update((t) => t.bands.splice(i + 1, 0, copy));
            }}
          >
            <Copy size={13} />
          </button>
          <button
            type="button"
            className="p-0.5 hover:text-F51D00"
            title="Delete section"
            onClick={(e) => {
              e.stopPropagation();
              if (!window.confirm(`Delete section "${b.name || "Section"}" and everything in it?`)) return;
              update((t) => (t.bands = t.bands.filter((x) => x.id !== b.id)));
              setSelection({ bandId: null, ids: [], colId: null });
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>
        {(b.elements || []).length > 0 && (
          <div className="ml-4 mb-1 flex flex-col border-l border-EAECF0">
            {/* topmost element first, like a layers list */}
            {[...b.elements].reverse().map((el) => {
              const sel = selection.ids.includes(el.id);
              return (
                <button
                  key={el.id}
                  type="button"
                  onClick={(e) =>
                    setSelection(
                      (e.shiftKey || e.metaKey || e.ctrlKey) && selection.bandId === b.id
                        ? { bandId: b.id, ids: sel ? selection.ids.filter((x) => x !== el.id) : [...selection.ids, el.id], colId: null }
                        : { bandId: b.id, ids: [el.id], colId: null }
                    )
                  }
                  className={`text-left pl-2 pr-1 py-0.5 text-[10px] flex gap-1 items-center ${sel ? "bg-D5E6Ff text-000000" : "text-344054 hover:bg-f3f8ff"}`}
                  title={elementLabel(el)}
                >
                  <span className="text-667085 shrink-0 w-12">{EL_NAMES[el.type] || el.type}</span>
                  <span className="truncate flex-1">{elementLabel(el)}</span>
                  {el.visibleIf && <span className="text-667085 shrink-0" title={"Shown only when " + el.visibleIf}>if</span>}
                </button>
              );
            })}
          </div>
        )}
        </React.Fragment>
      ))}
    </div>
  );
}

function FormulasTab({ template, sample, update }) {
  const vars = template.variables || [];
  const ctx = useMemo(() => ZP.createContext(template, sample), [template, sample]);
  const setVar = (i, patch, key) =>
    update(
      (t) => {
        t.variables[i] = { ...t.variables[i], ...patch };
      },
      { key }
    );
  return (
    <div className="p-3 flex flex-col gap-3">
      <p className="text-[10px] text-667085 leading-snug">
        Named formulas you can reuse anywhere as <span className="font-mono">var.name</span> (or just <span className="font-mono">name</span>). They can use
        fields, table columns (<span className="font-mono">SUM(col.amount)</span>), other variables, and row functions like{" "}
        <span className="font-mono">ROW(2, col.amount) - ROW(1, col.amount)</span>.
      </p>
      {vars.map((v, i) => {
        const r = ctx.evalSafe(v.expr, ctx.rootScope());
        return (
          <div key={i} className="border border-EAECF0 rounded p-2 flex flex-col gap-1.5 bg-white">
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-667085 font-mono">var.</span>
              <input
                className="flex-1 border border-EAECF0 rounded px-1.5 py-0.5 text-xs font-mono"
                value={v.name}
                placeholder="name"
                onChange={(e) => setVar(i, { name: e.target.value.replace(/[^A-Za-z0-9_]/g, "_") }, `var-name-${i}`)}
              />
              <button type="button" className="text-667085 hover:text-F51D00" onClick={() => update((t) => t.variables.splice(i, 1))} title="Delete">
                <Trash2 size={13} />
              </button>
            </div>
            <FormulaInput value={v.expr} onChange={(x) => setVar(i, { expr: x }, `var-expr-${i}`)} template={template} sample={sample} scope={{ kind: "doc" }} showResult={false} />
            <div className={`text-[10px] font-mono ${r.error ? "text-F51D00" : "text-667085"}`}>{r.error ? `⚠ ${r.error}` : `= ${ZP.toStr(r.value)}`}</div>
          </div>
        );
      })}
      <button
        type="button"
        className="flex items-center justify-center gap-1 border border-dashed border-primary text-primary rounded py-1.5 text-xs"
        onClick={() =>
          update((t) => {
            t.variables = t.variables || [];
            const names = new Set(t.variables.map((x) => x.name));
            let n = t.variables.length + 1;
            while (names.has(`value${n}`)) n++;
            t.variables.push({ name: `value${n}`, expr: "" });
          })
        }
      >
        <Plus size={14} /> Add formula
      </button>
    </div>
  );
}

const TABS = [
  { id: "insert", label: "Insert", icon: <Plus size={14} /> },
  { id: "fields", label: "Fields", icon: <Braces size={14} /> },
  { id: "sections", label: "Sections", icon: <PanelTop size={14} /> },
  { id: "formulas", label: "Formulas", icon: <Calculator size={14} /> },
];

export default function LeftPanel(props) {
  const [tab, setTab] = useState("insert");
  return (
    <div className="w-64 shrink-0 border-r border-EAECF0 bg-white flex flex-col min-h-0">
      <div className="grid grid-cols-4 border-b border-EAECF0">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex flex-col items-center gap-0.5 py-2 text-[10px] ${tab === t.id ? "text-primary border-b-2 border-primary font-semibold" : "text-667085 hover:text-000000"}`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        {tab === "insert" && <InsertTab {...props} />}
        {tab === "fields" && <FieldsTab {...props} />}
        {tab === "sections" && <SectionsTab {...props} />}
        {tab === "formulas" && <FormulasTab {...props} />}
      </div>
    </div>
  );
}
